import { Component, OnDestroy, ViewChild, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ApiPaginationResult,
  PaginationResult,
} from '../../../../shared/models/api-result.model';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Table, TableModule } from 'primeng/table';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { ButtonModule } from 'primeng/button';
import { ToolbarModule } from 'primeng/toolbar';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { RouterLink } from '@angular/router';
import { InputTextModule } from 'primeng/inputtext';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { FormsModule } from '@angular/forms';
import { ToastModule } from 'primeng/toast';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import {
  LateFeePolicy,
  LateFeeType,
  normalizeLateFeeType,
} from '../../../models/late-fee-policy.model';
import { LateFeePolicyService } from '../../../services/late-fee-policy.service';
import { PaginationService } from '../../../../shared/services/pagination.service';
import { I18nCurrencyPipe } from '../../../../shared/pipes/i18n-currency.pipe';

@Component({
  selector: 'app-late-fee-policy-list',
  imports: [
    FormsModule,
    InputTextModule,
    TableModule,
    IconFieldModule,
    InputIconModule,
    ButtonModule,
    ToolbarModule,
    BreadcrumbModule,
    ConfirmDialogModule,
    ProgressSpinnerModule,
    RouterLink,
    ToggleSwitchModule,
    ToastModule,
    I18nCurrencyPipe,
  ],
  providers: [ConfirmationService],
  templateUrl: './late-fee-policy-list.component.html',
})
export class LateFeePolicyListComponent implements OnDestroy {
  @ViewChild('table') table?: Table;

  private lateFeePolicyService = inject(LateFeePolicyService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  paginationService = inject(PaginationService);

  lateFeePolicies = signal<LateFeePolicy[]>([]);

  pageNumber = signal<number>(1);
  pageSize = signal<number>(10);
  totalRecords = signal<number>(0);
  totalPages = signal<number>(0);

  loading = signal(false);

  nameFilter?: string;

  readonly lateFeeType = LateFeeType;

  private destroyFilter$ = new Subject<void>();
  private filterChanged$ = new Subject<void>();

  constructor() {
    this.filterChanged$
      .pipe(debounceTime(500), takeUntil(this.destroyFilter$))
      .subscribe(() => {
        this.pageNumber.set(0);
        this.table?.reset();
      });
  }

  ngOnDestroy(): void {
    this.destroyFilter$.next();
    this.destroyFilter$.complete();
  }

  onFilterChange(): void {
    this.filterChanged$.next();
  }

  loadData(event: any): void {
    const page = this.paginationService.mapLazyEventToPageRequest(
      event,
      this.pageSize()
    );
    this.pageNumber.set(page.pageNumber);
    this.pageSize.set(page.pageSize);

    this.loading.set(true);

    this.lateFeePolicyService
      .getAll(this.pageNumber(), this.pageSize(), this.nameFilter)
      .subscribe({
        next: (response: ApiPaginationResult) => {
          this.loading.set(false);

          const page = response.data as PaginationResult;
          this.lateFeePolicies.set(
            (page.items as LateFeePolicy[]).map((item) => ({
              ...item,
              type: normalizeLateFeeType(item.type) as LateFeeType,
            }))
          );
          this.pageNumber.set(page.pageNumber);
          this.pageSize.set(page.pageSize);
          this.totalRecords.set(page.totalCount);
          this.totalPages.set(page.totalPages);
        },
        error: (e) => {
          this.loading.set(false);

          if (e instanceof HttpErrorResponse) {
            this.messageService.add({
              severity: 'error',
              summary: 'Falha',
              detail: 'Ocorreu um erro ao carregar as multas',
              life: 3000,
            });
          }
        },
      });
  }

  requestUpdateStatus(item: LateFeePolicy, event: any): void {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: 'Você tem certeza que quer alterar o status da multa?',
      header: 'Confirmação',
      closable: true,
      closeOnEscape: true,
      icon: 'pi pi-exclamation-triangle',
      rejectButtonProps: {
        label: 'Cancelar',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Alterar',
      },
      accept: () => {
        this.updateStatus(item);
      },
    });
  }

  private updateStatus(item: LateFeePolicy): void {
    this.loading.set(true);

    this.lateFeePolicyService.updateStatus(item.id, !item.isActive).subscribe({
      next: () => {
        this.loading.set(false);

        item.isActive = !item.isActive;

        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Status atualizado com sucesso',
          life: 2000,
        });
      },
      error: (e) => {
        this.loading.set(false);

        if (e instanceof HttpErrorResponse) {
          this.messageService.add({
            severity: 'error',
            summary: 'Falha',
            detail: 'Ocorreu um erro ao atualizar o status',
            life: 3000,
          });
        }
      },
    });
  }
}
